import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Coins,
  Plus,
  Search,
  RefreshCw,
  X,
  ChevronDown,
  ChevronRight,
  Building2,
  TrendingUp,
  TrendingDown,
  Zap,
  Download,
  AlertTriangle,
  Users,
  CheckCircle2,
  Clock,
  Activity,
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';

interface HoAdmin {
  user_id: number;
  name: string;
  username?: string;
  email: string;
  balance: number | null;
  role_level?: string;
  is_system_admin?: boolean;
  total_recharged: number;
  consumed: number;
  last_recharged: string | null;
  utilization_pct: number;
}

interface CompanyToken {
  company_id: number;
  company_name: string;
  company_code: string;
  ho_admins_count: number;
  total_balance: number | null;
  total_recharged: number;
  total_consumed: number;
  total_issued_to_company?: number;
  company_pending_tokens?: number;
  ho_user_required?: boolean;
  ho_user_required_message?: string;
  auto_mapped_tokens?: number;
  ho_admins: HoAdmin[];
}

interface BatchAssignment {
  user_id: number;
  tokens: number;
}

const TAB_PRICING = [
  { key: 'download_raw_data', label: 'Download Raw Data', cost: 1, color: 'blue' },
  { key: 'generate_alerts', label: 'Generate Alerts', cost: 1, color: 'amber' },
  { key: 'run_rtc_optimization', label: 'RTC Optimization', cost: 2, color: 'purple' },
];

function UtilBar({ pct }: { pct: number }) {
  const clamped = Math.min(100, Math.max(0, pct));
  const color = clamped >= 90 ? '#EF4444' : clamped >= 60 ? '#F59E0B' : '#22C55E';
  return (
    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div style={{ width: `${clamped}%`, background: color }} className="h-full rounded-full transition-all" />
    </div>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

const TokenManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState<CompanyToken[]>([]);
  const [globalTotals, setGlobalTotals] = useState({
    total_balance: 0,
    total_recharged: 0,
    total_consumed: 0,
    total_pending: 0,
    total_issued_to_companies: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState<Set<number>>(new Set());
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyToken | null>(null);
  const [selectedUser, setSelectedUser] = useState<HoAdmin | null>(null);
  const [assignmentMode, setAssignmentMode] = useState<'total' | 'per-tab'>('total');
  const [tokenAmount, setTokenAmount] = useState('200');
  const [tabTokens, setTabTokens] = useState({ download_raw_data: '100', generate_alerts: '100', run_rtc_optimization: '100' });
  const [batchAssignments, setBatchAssignments] = useState<BatchAssignment[]>([]);
  const [batchAmount, setBatchAmount] = useState('200');

  const roleName = String((currentUser as any)?.roleName || currentUser?.role || '').toLowerCase();
  const isSystemAdmin =
    !!currentUser?.isGlobalAdmin ||
    !!(currentUser as any)?.is_superuser ||
    !!(currentUser as any)?.is_super_admin ||
    ['system_admin', 'super_admin'].includes(roleName);

  useEffect(() => {
    if (isSystemAdmin) void fetchData();
  }, [isSystemAdmin]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsUpdating(true);
    try {
      const resp = await backendApi.getAdminCompanyTokenWallets();
      if (resp.success) {
        setCompanies(resp.companies || []);
        if (resp.totals) setGlobalTotals(resp.totals);
        // Auto-expand all companies on first load
        if (!silent && resp.companies?.length > 0) {
          setExpandedCompanies(new Set(resp.companies.map((c: CompanyToken) => c.company_id)));
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); setIsUpdating(false); }
  };

  const toggleExpand = (id: number) => {
    setExpandedCompanies(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const openAssign = (company: CompanyToken, user: HoAdmin) => {
    setSelectedCompany(company);
    setSelectedUser(user);
    setAssignmentMode('total');
    setTokenAmount('200');
    setTabTokens({ download_raw_data: '100', generate_alerts: '100', run_rtc_optimization: '100' });
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    if (!selectedCompany || !selectedUser) return;
    const total = assignmentMode === 'total'
      ? parseInt(tokenAmount) || 0
      : (parseInt(tabTokens.download_raw_data) || 0) + (parseInt(tabTokens.generate_alerts) || 0) + (parseInt(tabTokens.run_rtc_optimization) || 0);
    if (total <= 0) { alert('Token amount must be > 0'); return; }
    setIsUpdating(true);
    try {
      const resp = await backendApi.adminAssignCompanyTokens({ company_id: selectedCompany.company_id, user_id: selectedUser.user_id, tokens: total });
      if (resp.success) { setShowAssignModal(false); await fetchData(true); }
      else alert(resp.error || 'Failed');
    } catch (e: any) { alert(e.message); }
    finally { setIsUpdating(false); }
  };

  const toggleBatch = (user: HoAdmin) => {
    setBatchAssignments(prev =>
      prev.some(a => a.user_id === user.user_id)
        ? prev.filter(a => a.user_id !== user.user_id)
        : [...prev, { user_id: user.user_id, tokens: 0 }]
    );
  };

  const handleBatchAssign = async () => {
    if (!batchAssignments.length) { alert('Select at least one user'); return; }
    const amt = parseInt(batchAmount) || 0;
    if (amt <= 0) { alert('Amount must be > 0'); return; }
    setIsUpdating(true);
    try {
      const resp = await backendApi.adminBatchAssignTokens({ assignments: batchAssignments.map(a => ({ user_id: a.user_id, tokens: amt })) });
      if (resp.success) {
        alert(`Done: ${resp.summary?.success_count} success, ${resp.summary?.failed_count} failed`);
        setShowBatchModal(false);
        setBatchAssignments([]);
        await fetchData(true);
      } else alert(resp.error || 'Failed');
    } catch (e: any) { alert(e.message); }
    finally { setIsUpdating(false); }
  };

  const filtered = companies.filter(c =>
    !searchTerm ||
    c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalTabRuns = {
    download: Math.floor(globalTotals.total_consumed / 1),
    alerts: Math.floor(globalTotals.total_consumed / 1),
    rtc: Math.floor(globalTotals.total_consumed / 2),
  };

  if (!isSystemAdmin) {
    return (
      <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-4">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><Coins size={28} className="text-red-500" /></div>
          <h2 className="text-xl font-bold text-gray-800">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only System Admins can manage customer tokens.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading token data...</p>
        </div>
      </div>
    );
  }

  const perTabTotal = (parseInt(tabTokens.download_raw_data) || 0) + (parseInt(tabTokens.generate_alerts) || 0) + (parseInt(tabTokens.run_rtc_optimization) || 0);

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-10 px-2 sm:px-4">
      <style>{`
        .token-stat-bold-label {
          font-weight: 600 !important;
          color: #111827 !important;
        }
        .token-stat-bold-sub {
          font-weight: 500 !important;
          color: #374151 !important;
        }
        .token-stat-bold-cost {
          font-weight: 500 !important;
          color: #374151 !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto space-y-5">

        {/* ── HERO ─────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-5">
          <div className="admin-toolbar">
            <div className="space-y-1">
              <p className="admin-eyebrow">Simulation tokens</p>
              <div className="flex items-center gap-3">
                <div className="hidden sm:flex w-11 h-11 rounded-[14px] bg-gradient-to-br from-[#0084cf] to-[#1C4D88] items-center justify-center text-white shadow-lg">
                  <Coins size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Token Management</h1>
                  <p className="admin-page-subtitle">Assign, track and audit simulation tokens across all customer companies.</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => fetchData(true)} className="admin-button admin-button-secondary">
                <RefreshCw size={15} className={isUpdating ? 'animate-spin' : ''} /> Refresh
              </button>
              <button onClick={() => setShowBatchModal(true)} className="admin-button admin-button-primary">
                <Plus size={16} /> Batch Assign
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── GLOBAL SUMMARY (SECOND SECTION) ─────────── */}
        <motion.section initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Global Token Summary</h2>
              <p className="admin-section-copy">Live token distribution and tab execution pricing across all customer companies.</p>
            </div>
          </div>
          <div className="admin-section-body space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
              {[
                { label: 'Companies', value: companies.length, sub: 'with token wallets', icon: <Building2 size={18} />, color: 'text-[#1C4D88] bg-[#F2F8FC]' },
                { label: 'Company Issued', value: (globalTotals.total_issued_to_companies || 0).toLocaleString(), sub: 'tokens issued on company name', icon: <TrendingUp size={18} />, color: 'text-green-700 bg-green-50' },
                { label: 'Active Balance', value: globalTotals.total_balance.toLocaleString(), sub: 'tokens remaining', icon: <Coins size={18} />, color: 'text-[#1C4D88] bg-[#F2F8FC]' },
                { label: 'Mapped to HO', value: globalTotals.total_recharged.toLocaleString(), sub: 'currently available to HO users', icon: <Users size={18} />, color: 'text-[#1C4D88] bg-[#F2F8FC]' },
                { label: 'Pending (No HO)', value: (globalTotals.total_pending || 0).toLocaleString(), sub: 'create HO user to activate', icon: <Clock size={18} />, color: 'text-amber-700 bg-amber-50' },
                { label: 'Total Consumed', value: globalTotals.total_consumed.toLocaleString(), sub: 'tokens used', icon: <Activity size={18} />, color: 'text-red-700 bg-red-50' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-200 px-3 py-3 flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.color}`}>{s.icon}</div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.05em] token-stat-bold-label">{s.label}</p>
                    <p className="text-xl font-black text-gray-900 leading-tight mt-1">{s.value}</p>
                    <p className="text-xs token-stat-bold-sub mt-1">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {TAB_PRICING.map((t) => (
                <div key={t.key} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-gray-900">{t.label}</p>
                    <p className="text-xs token-stat-bold-cost mt-1">Per execution cost</p>
                  </div>
                  <div className={`text-lg font-black ${t.color === 'blue' ? 'text-[#1C4D88]' : t.color === 'amber' ? 'text-amber-700' : 'text-[#1C4D88]'}`}>
                    {t.cost} token{t.cost > 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ── SEARCH ───────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.09 }}
          className="admin-section"
        >
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter companies</h2>
              <p className="admin-section-copy">Search by company name or code.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
              <div className="admin-search-wrap w-full md:max-w-md">
                <Search size={18} />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search company name or code"
                  className="admin-input"
                />
              </div>
              <div className="flex items-center gap-2 justify-end w-full md:w-auto">
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="admin-button admin-button-ghost w-full md:w-auto !min-h-[2.9rem] !px-4 !font-black"
                  >
                    Clear
                  </button>
                )}
                <span className="text-xs font-black text-[#4D637D] bg-[#EEF4FB] border border-[#D5E2F1] rounded-full px-3.5 py-1.5 whitespace-nowrap">
                  {filtered.length} companies
                </span>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── COMPANY CARDS ────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-3">
          {filtered.length === 0 ? (
            <div className="admin-section"><div className="admin-section-body"><div className="admin-empty">No companies found.</div></div></div>
          ) : filtered.map((company, i) => {
            const expanded = expandedCompanies.has(company.company_id);
            const issuedForCompany = Number(company.total_issued_to_company || 0);
            const utilPct = issuedForCompany > 0 ? Math.round((company.total_consumed / issuedForCompany) * 100) : 0;
            const remaining = company.total_balance;
            const pendingForCompany = Number(company.company_pending_tokens || 0);

            return (
              <motion.div
                key={company.company_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 8) * 0.04 }}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                {/* Company Header Row */}
                <button
                  onClick={() => toggleExpand(company.company_id)}
                  className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${(remaining === null || remaining > 0) ? 'bg-[#E3F0F9] text-[#0066A3]' : 'bg-gray-100 text-gray-400'}`}>
                    <Building2 size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-[#10213A] text-sm">{company.company_name}</span>
                      <span className="text-[10px] font-mono font-black text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{company.company_code}</span>
                      <span className="text-[10px] font-black text-gray-600">{company.ho_admins_count} HO Admin{company.ho_admins_count !== 1 ? 's' : ''}</span>
                      {pendingForCompany > 0 && (
                        <span className="text-[10px] font-black text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                          {pendingForCompany} pending (HO required)
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3">
                      <UtilBar pct={utilPct} />
                      <span className="text-[10px] font-black text-gray-600 flex-shrink-0">{utilPct}% used</span>
                    </div>
                  </div>

                  {/* Compact Stats */}
                  <div className="hidden md:flex items-center gap-6 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-500 uppercase">Issued (Company)</p>
                      <p className="text-base font-black text-gray-800">{issuedForCompany.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-500 uppercase">Mapped to HO</p>
                      <p className="text-base font-black text-[#1C4D88]">{(company.total_recharged ?? 0).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-500 uppercase">Pending</p>
                      <p className={`text-base font-black ${pendingForCompany > 0 ? 'text-orange-600' : 'text-gray-400'}`}>{pendingForCompany.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-500 uppercase">Balance</p>
                      <p className={`text-base font-black ${remaining === null ? 'text-emerald-600' : remaining > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                        {remaining === null ? 'Unlimited' : remaining.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-500 uppercase">Consumed</p>
                      <p className="text-base font-black text-red-500">{company.total_consumed.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-gray-400 ml-2">
                    {expanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                  </div>
                </button>

                {/* Mobile inline stats */}
                <div className="md:hidden px-5 pb-3 flex flex-wrap gap-4 text-sm font-bold">
                  <span className="text-gray-600">Issued: <strong className="font-black">{issuedForCompany.toLocaleString()}</strong></span>
                  <span className="text-[#1C4D88]">Mapped: <strong className="font-black">{(company.total_recharged ?? 0).toLocaleString()}</strong></span>
                  <span className="text-orange-600">Pending: <strong className="font-black">{pendingForCompany.toLocaleString()}</strong></span>
                  <span className="text-amber-600">Balance: <strong className="font-black">{remaining === null ? 'Unlimited' : remaining.toLocaleString()}</strong></span>
                  <span className="text-red-500">Used: <strong className="font-black">{company.total_consumed.toLocaleString()}</strong></span>
                </div>

                {/* Expanded User Rows */}
                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden border-t border-gray-100"
                    >
                      {company.ho_admins.length === 0 ? (
                        <div className="px-5 py-5 text-sm text-center">
                          <p className="text-gray-500 font-semibold">No HO Admin users for this company.</p>
                          {pendingForCompany > 0 && (
                            <p className="mt-2 text-orange-700 bg-orange-100 border border-orange-200 rounded-md px-3 py-2 inline-block font-bold">
                              {company.ho_user_required_message || 'Pending company tokens will be visible once an HO Admin user is available.'}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {/* User header */}
                          <div className="hidden md:grid grid-cols-[auto_1fr_120px_120px_120px_140px_auto] gap-4 px-5 py-2 bg-gray-50 text-[10px] font-black text-gray-400 uppercase">
                            <span></span>
                            <span>User</span>
                            <span className="text-right">Assigned</span>
                            <span className="text-right">Balance</span>
                            <span className="text-right">Consumed</span>
                            <span>Utilization</span>
                            <span></span>
                          </div>

                          {company.ho_admins.map(admin => (
                            <div key={admin.user_id} className="px-5 py-3 flex flex-col md:grid md:grid-cols-[auto_1fr_120px_120px_120px_140px_auto] md:items-center gap-3 md:gap-4 hover:bg-amber-50/30 transition-colors">
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={batchAssignments.some(a => a.user_id === admin.user_id)}
                                onChange={() => toggleBatch(admin)}
                                className="w-4 h-4 text-amber-500 rounded"
                              />

                              {/* Name + Email */}
                              <div className="min-w-0">
                                <p className="font-bold text-sm text-[#10213A] truncate">{admin.name}</p>
                                <p className="text-[10px] text-[#1C4D88] font-bold truncate">@{admin.username || 'ho_admin'}</p>
                                <p className="text-xs text-gray-500 truncate">{admin.email}</p>
                                {admin.last_recharged && (
                                  <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                                    <Clock size={9} /> Last recharged: {formatDate(admin.last_recharged)}
                                  </p>
                                )}
                              </div>

                              {/* Stats */}
                              <div className="md:text-right">
                                <p className="text-[10px] font-bold text-gray-400 uppercase md:hidden inline-block mr-1">Assigned</p>
                                <span className="text-sm font-black text-gray-700">{(admin.total_recharged ?? 0).toLocaleString()}</span>
                              </div>
                              <div className="md:text-right">
                                <p className="text-[10px] font-bold text-gray-400 uppercase md:hidden inline-block mr-1">Balance</p>
                                <span className={`text-sm font-black ${admin.balance === null ? 'text-emerald-600' : admin.balance > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                                  {admin.balance === null ? 'Unlimited' : admin.balance.toLocaleString()}
                                </span>
                              </div>
                              <div className="md:text-right">
                                <p className="text-[10px] font-bold text-gray-400 uppercase md:hidden inline-block mr-1">Consumed</p>
                                <span className="text-sm font-black text-red-500">{admin.consumed.toLocaleString()}</span>
                              </div>

                              {/* Utilization */}
                              <div className="space-y-1">
                                <UtilBar pct={admin.utilization_pct} />
                                <p className="text-[10px] text-gray-400">
                                  {admin.utilization_pct}% used
                                  {admin.balance === 0 && <span className="ml-1 text-red-500 font-bold">— No tokens!</span>}
                                  {admin.balance === null && <span className="ml-1 text-emerald-600 font-bold">— Unlimited runs</span>}
                                </p>
                              </div>

                              {/* Assign Button */}
                              <button
                                onClick={() => openAssign(company, admin)}
                                className="flex-shrink-0 px-3 py-1.5 text-xs font-black bg-[#0084cf] hover:bg-[#1C4D88] text-white rounded-lg flex items-center gap-1 transition-colors"
                              >
                                <Plus size={13} /> Assign
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ── ASSIGN MODAL ─────────────────────────────── */}
        <AnimatePresence>
          {showAssignModal && selectedCompany && selectedUser && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAssignModal(false)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                onClick={e => e.stopPropagation()}
                className="admin-modal-shell w-full max-w-lg overflow-hidden"
              >
                <div className="admin-modal-header flex items-start justify-between">
                  <div>
                    <p className="admin-eyebrow">Token assignment</p>
                    <h3 className="admin-modal-title text-[1.25rem] text-[#10213A] mt-1">Assign tokens</h3>
                  </div>
                  <button onClick={() => setShowAssignModal(false)} className="admin-icon-button"><X size={17} /></button>
                </div>

                <div className="admin-modal-body space-y-4">
                  {/* User info */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-200">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-black text-sm flex-shrink-0">
                      {selectedUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-[#10213A]">{selectedUser.name}</p>
                      <p className="text-xs text-gray-500">{selectedUser.email}</p>
                      <p className="text-xs text-gray-500">{selectedCompany.company_name}</p>
                    </div>
                    <div className="ml-auto text-right flex-shrink-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Current Balance</p>
                      <p className="text-xl font-black text-amber-600">{selectedUser.balance === null ? 'Unlimited' : selectedUser.balance}</p>
                    </div>
                  </div>

                  {/* Wallet snapshot */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-green-700 uppercase">Assigned</p>
                      <p className="text-base font-black text-green-800">{(selectedUser.total_recharged ?? 0)}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-amber-700 uppercase">Balance</p>
                      <p className="text-base font-black text-amber-800">{selectedUser.balance === null ? 'Unlimited' : selectedUser.balance}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                      <p className="text-[10px] font-bold text-red-700 uppercase">Used</p>
                      <p className="text-base font-black text-red-800">{selectedUser.consumed}</p>
                    </div>
                  </div>

                  {selectedUser.is_system_admin ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-800 text-center font-bold">
                      <p className="text-sm">This user has the System Admin role.</p>
                      <p className="text-xs mt-1 text-emerald-600">System Administrators have unlimited tokens. Manual token assignment is not required.</p>
                    </div>
                  ) : (
                    <>
                      {/* Mode selector */}
                      <div>
                        <p className="text-xs font-bold text-gray-600 mb-2">Assignment mode</p>
                        <div className="flex gap-2">
                          {['total', 'per-tab'].map(m => (
                            <button
                              key={m}
                              onClick={() => setAssignmentMode(m as 'total' | 'per-tab')}
                              className={`flex-1 py-1.5 text-xs font-black rounded-lg border transition-colors ${assignmentMode === m ? 'bg-amber-500 border-amber-500 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                              {m === 'total' ? 'Total amount' : 'Per-tab split'}
                            </button>
                          ))}
                        </div>
                      </div>

                      {assignmentMode === 'total' ? (
                        <div>
                          <label className="text-xs font-bold text-gray-600">Tokens to add</label>
                          <input
                            type="number" min="1" value={tokenAmount}
                            onChange={e => setTokenAmount(e.target.value)}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-black text-[#10213A] focus:ring-2 focus:ring-amber-400"
                          />
                          <p className="text-[10px] text-gray-400 mt-1">Balance after: {selectedUser.balance === null ? 'Unlimited' : (selectedUser.balance ?? 0) + (parseInt(tokenAmount) || 0)}</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-[10px] text-[#1C4D88] bg-[#F2F8FC] border border-[#C2DFF1] rounded px-2 py-1 font-bold">
                            Tokens are pooled — tab amounts help you plan the allocation.
                          </p>
                          {TAB_PRICING.map(t => (
                            <div key={t.key} className="flex items-center gap-3">
                              <span className="text-sm w-36 flex-shrink-0 font-bold text-[#10213A]">{t.label}</span>
                              <input
                                type="number" min="0" step="10"
                                value={tabTokens[t.key as keyof typeof tabTokens]}
                                onChange={e => setTabTokens({ ...tabTokens, [t.key]: e.target.value })}
                                className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm font-bold focus:ring-2 focus:ring-amber-400"
                              />
                              <span className="text-xs text-gray-400 flex-shrink-0">= {Math.floor((parseInt(tabTokens[t.key as keyof typeof tabTokens]) || 0) / t.cost)} runs</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-black bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <span>Total to assign:</span>
                            <span className="text-amber-700">{perTabTotal} tokens</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="admin-modal-footer gap-2">
                  <button onClick={() => setShowAssignModal(false)} className="admin-button admin-button-ghost">Cancel</button>
                  <button onClick={handleAssign} disabled={isUpdating || !!selectedUser.is_system_admin} className="admin-button admin-button-primary">
                    {isUpdating ? 'Assigning…' : selectedUser.is_system_admin ? 'Unlimited Tokens Active' : `Assign ${assignmentMode === 'total' ? tokenAmount : perTabTotal} tokens`}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* ── BATCH MODAL ──────────────────────────────── */}
        <AnimatePresence>
          {showBatchModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBatchModal(false)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                onClick={e => e.stopPropagation()}
                className="admin-modal-shell w-full max-w-2xl flex flex-col max-h-[80vh] overflow-hidden"
              >
                <div className="admin-modal-header flex items-start justify-between">
                  <div>
                    <p className="admin-eyebrow">Batch assignment</p>
                    <h3 className="admin-modal-title text-[1.25rem] text-[#10213A] mt-1">Assign to multiple users</h3>
                  </div>
                  <button onClick={() => setShowBatchModal(false)} className="admin-icon-button"><X size={17} /></button>
                </div>

                <div className="admin-modal-body flex-1 overflow-y-auto space-y-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600">Tokens per user</label>
                    <input
                      type="number" min="1" value={batchAmount}
                      onChange={e => setBatchAmount(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-black focus:ring-2 focus:ring-amber-400"
                    />
                  </div>

                  <p className="text-xs font-bold text-gray-600">
                    Select users ({batchAssignments.length} selected)
                  </p>

                  <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                    {companies.map(company => (
                      <div key={company.company_id}>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-black text-gray-700">{company.company_name}</span>
                          <span className="ml-2 text-[10px] text-gray-400 font-mono">{company.company_code}</span>
                        </div>
                        {company.ho_admins.length === 0 ? (
                          <div className="px-4 py-2 text-xs text-gray-500">
                            No HO Admin users.
                            {Number(company.company_pending_tokens || 0) > 0 && (
                              <span className="ml-1 text-orange-700 font-bold">
                                Pending tokens: {Number(company.company_pending_tokens || 0)} (will map once an HO Admin user is available).
                              </span>
                            )}
                          </div>
                        ) : company.ho_admins.map(admin => (
                          <label key={admin.user_id} className={`flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 cursor-pointer border-b border-gray-100 ${admin.is_system_admin ? 'opacity-80 cursor-default hover:bg-transparent' : ''}`}>
                            <input
                              type="checkbox"
                              disabled={!!admin.is_system_admin}
                              checked={batchAssignments.some(a => a.user_id === admin.user_id)}
                              onChange={() => toggleBatch(admin)}
                              className="w-4 h-4 text-amber-500 rounded disabled:opacity-50"
                            />
                            <span className="flex-1 text-sm text-gray-700">
                              {admin.name} <span className="text-[10px] text-[#1C4D88] font-bold">(@{admin.username || (admin.is_system_admin ? 'system_admin' : 'ho_admin')})</span>
                              {admin.is_system_admin && <span className="ml-1 text-[10px] font-black text-emerald-600 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">Unlimited</span>}
                            </span>
                            <span className="text-xs text-gray-400">Balance: {admin.balance === null ? 'Unlimited' : admin.balance}</span>
                            <span className="text-xs text-red-400">Used: {admin.consumed}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="admin-modal-footer gap-2">
                  <button onClick={() => setShowBatchModal(false)} className="admin-button admin-button-ghost">Cancel</button>
                  <button
                    onClick={handleBatchAssign}
                    disabled={isUpdating || !batchAssignments.length}
                    className="admin-button admin-button-primary"
                  >
                    {isUpdating ? 'Assigning…' : `Assign ${batchAmount} tokens to ${batchAssignments.length} user${batchAssignments.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default TokenManagement;
